import withRouter from "Components/Common/withRouter"
import React, { useEffect, useMemo, useState } from "react"
import ProjectDetailsTemplate from "Components/templates/project-managment/project-details"
import Project, { ProjectFinance } from "models/project"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { navigateToProjectList } from "utils/navigator"
import { getProject, getProjectHealth, getProjectHealthHistory, syncProject, updateProjectFinance } from "slices/thunk"
import { ProjectHealth, ProjectHealthHistoryItem } from "models/project-health"
import useProjectMarginTarget from "hooks/useProjectMarginTarget"
import Transaction, { TransactionUI } from "models/transaction"
import ProjectTask, { ProjectTaskUI } from "models/project-task"
import ProjectBudget from "models/project-budget"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { formatCurrency, formatNumber } from "utils/tools"
import { ChangeOrder } from "models/change-order"
import ClientProjectFinance, { ClientProjectFinanceUI } from "models/project-finance"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import CurrencyInput from "Components/molecule/inputs/currency-input"
import { useDiffStore } from "hooks/useInputDiffStore"
import { FinancialAndForecastingRow } from "Components/organisms/financials-and-forecasting"
import { useNavigationGuard } from "../../../../context/navigation-guard-provider";
import useManual from "hooks/useManual"
import { CheckCircleIcon, CloseCircleIcon } from "Components/atoms/icons"


const PROJECT_FORECAST_CHANGES = 'project_forecast_changes'
const DIRTY_INPUT_CLASS = 'forecast-input--dirty'
const FINANCIALS_AND_FORECASTING_CONTROLS = 'financials-and-forecasting';


const ProjectDetailsPage = (props: any) => {
    document.title = "Project | IbexIQ";

    const { handleOpenManual } = useManual("project-managment/projects");
    const [searchParams] = useSearchParams();
    const [loadingProject, setLoadingProject] = useState<boolean>(true);
    const [isSyncingData, setIsSyncingData] = useState<boolean>(false);
    const [isSyncModalOpen, setIsSyncModalOpen] = useState<boolean>(false)
    const [project, setProject] = useState<Project | null>(null);
    const [projectTransactions, setProjectTransactions] = useState<Transaction[]>([]);
    const [projectTasks, setProjectTasks] = useState<ProjectTask[]>([]);
    const [projectBudgets, setProjectBudget] = useState<ProjectBudget[]>([]);
    const [projectFinanceAndForecasting, setProjectFinanceAndForecasting] = useState<ClientProjectFinance[]>([]);
    const [projectFinance, setProjectFinance] = useState<ProjectFinance | null>(null)
    const [activeTab, setActiveTab] = useState("Health");
    const [projectHealth, setProjectHealth] = useState<ProjectHealth | null>(null);
    const [loadingProjectHealth, setLoadingProjectHealth] = useState<boolean>(true);
    const [projectHealthHistory, setProjectHealthHistory] = useState<ProjectHealthHistoryItem[]>([]);
    const marginTarget = useProjectMarginTarget();
    const { setIsDirty } = useNavigationGuard()
    const {
        addChange,
        removeChange,
        getStoredChanges,
        commitChanges,
    } = useDiffStore<number>({
        storageKey: PROJECT_FORECAST_CHANGES,
        dirtyClass: DIRTY_INPUT_CLASS
    })


    const projectId = useMemo(() => searchParams.get("projectId"), [searchParams]);

    const revenueBudgets = useMemo(() => projectBudgets.filter(
        b => b.projectBudgetType?.description === "Income"
    ), [projectBudgets])

    const costBudgets = useMemo(() => projectBudgets.filter(
        b => b.projectBudgetType?.description === "Expense"
    ), [projectBudgets])


    const financialAndForecastingRows: FinancialAndForecastingRow[] = useMemo(() => {

        let totalAmount = 0
        let totalForecast = 0
        let totalPlanned = 0

        for (let i = 0; i < projectFinanceAndForecasting.length; i++) {
            totalAmount += Number(projectFinanceAndForecasting[i].actualAmount ?? 0)
            totalForecast += Number(projectFinanceAndForecasting[i].forecastAmount ?? 0)
            totalPlanned += Number(projectFinanceAndForecasting[i].plannedAmount ?? 0)
        }


        return [...projectFinanceAndForecasting.map(p => ({
            ...p,
            __rowType: 'client-project-finance' as const  // add "as const" here
        })), {
            __rowType: 'summary' as const,               // and here too for consistency
            plannedAmount: totalPlanned,
            actualAmount: totalAmount,
            forecastAmount: totalForecast
        }];
    }, [projectFinanceAndForecasting])

    const updateSummaryRow = () => {
        const storedChanges = getStoredChanges();

        let totalActual = 0;
        let totalForecast = 0;

        for (const pf of projectFinanceAndForecasting) {
            const key = `${pf.projectId}+${pf.fiscalPeriod}`;
            totalActual += Number(pf.actualAmount ?? 0);
            totalForecast += Number(storedChanges[key] !== undefined ? storedChanges[key] : (pf.forecastAmount ?? 0));
        }

        const forecastCell = document.getElementById('summary-forecast');
        const forecastVarianceCell = document.getElementById('summary-forecast-variance');
        const forecastVariance = totalActual - totalForecast;

        if (forecastCell) forecastCell.innerHTML = formatCurrency(totalForecast);
        if (forecastVarianceCell) {
            forecastVarianceCell.innerHTML = formatCurrency(forecastVariance);
            forecastVarianceCell.className = ClientProjectFinanceUI.varianceColor(forecastVariance);
        }
    };

    const loadProjectHealth = async (projectId: string) => {
        setLoadingProjectHealth(true);
        const [health, history] = await Promise.all([getProjectHealth(projectId), getProjectHealthHistory(projectId)]);
        setProjectHealth(health);
        setProjectHealthHistory(history ?? []);
        setLoadingProjectHealth(false);
    }

    const initializePage = async (projectId: string) => {
        loadProjectHealth(projectId);
        const projRes = await getProject(projectId);

        if (!projRes) {
            props.router.navigate(-1);
            return;
        }
        const { budgets, tasks, transactions, ...project } = projRes;

        if (!budgets || !tasks || !transactions) {
            props.router.navigate(-1);
            return;
        }

        setProject(project);
        setProjectBudget(budgets)
        setProjectTasks(tasks)
        setProjectTransactions(transactions)

        const projectFinance = new ProjectFinance({ ...project, budgets, tasks, transactions });

        setProjectFinance(projectFinance)
        setProjectFinanceAndForecasting(projectFinance.financialAndForecasting)
        setLoadingProject(false)
    }

    useEffect(() => {
        if (projectId) {
            initializePage(projectId)
        } else {
            toast.error("Something went wrong. Project Id not found")
            navigateToProjectList(props.router.navigate)
        }
    }, [projectId])

    const closeSyncModal = () => setIsSyncModalOpen(false)
    const openSyncModal = () => setIsSyncModalOpen(true)

    const handleSyncData = async (projecetId: string) => {
        setIsSyncingData(true);
        setIsSyncModalOpen(false);
        await syncProject(projecetId)
        setIsSyncingData(false);
        loadProjectHealth(projecetId);
    }

    const handleNavigateToAcumatica = (project: Project) => {
        if (!process.env.REACT_APP_ACUMATICA_PROJECT_URL) {
            toast.error("Could not find Acumatica URL.")
            return
        }

        const url = process.env.REACT_APP_ACUMATICA_PROJECT_URL + encodeURIComponent(project.code)
        window.open(url, "_blank")
    }

    const handleShowFinancialAndForecastingControls = () => {
        const controlsDiv = document.getElementById(FINANCIALS_AND_FORECASTING_CONTROLS);
        if (controlsDiv)
            controlsDiv.style.display = 'block'
    }

    const handleHideFinancialAndForecastingControls = () => {
        const controlsDiv = document.getElementById(FINANCIALS_AND_FORECASTING_CONTROLS);
        if (controlsDiv)
            controlsDiv.style.display = 'none'

    }

    const updateForecastVarianceCell = (
        projectId: string,
        fiscalPeriod: number
    ) => {
        const key = `${projectId}+${fiscalPeriod}`;
        const storedChanges = getStoredChanges();

        const pf = projectFinanceAndForecasting.find(
            p =>
                p.projectId === projectId &&
                p.fiscalPeriod === fiscalPeriod
        );

        if (!pf) return;

        const effectiveForecast =
            storedChanges[key] !== undefined
                ? storedChanges[key]
                : (pf.forecastAmount ?? 0);

        const variance =
            (pf.actualAmount ?? 0) - effectiveForecast;

        const varianceCell = document.getElementById(
            `forecast-variance-${fiscalPeriod}`
        ) as HTMLElement | null;


        if (!varianceCell) return;

        varianceCell.innerHTML = formatCurrency(variance);

        varianceCell.classList.remove("error", "success");
        varianceCell.classList.add(
            ClientProjectFinanceUI.varianceColor(variance)
        );
    };


    const handleAddChange = (
        projectId: string,
        fiscalPeriod: number,
        amount: number
    ) => {
        if (amount < 0) {
            toast.error("Value must be positive");
            return;
        }

        const inputId = `${projectId}+${fiscalPeriod}`;

        addChange(inputId, amount);

        // Show controls
        handleShowFinancialAndForecastingControls()
        updateForecastVarianceCell(projectId, fiscalPeriod)
        updateSummaryRow();
        setIsDirty(true);
    };


    const handleResetProjectFinancialAndForecasting = () => {
        const storedChanges = getStoredChanges();

        const keys = Object.keys(storedChanges);
        for (let i = 0; i < keys.length; i++) {
            const [projectId, fiscalPeriodStr] = keys[i].split("+");
            let newValue = 0;
            const pF = projectFinanceAndForecasting.find(pF => pF.fiscalPeriod === parseFloat(fiscalPeriodStr));
            newValue = pF ? pF.forecastAmount : 0;

            removeChange(keys[i], "$" + newValue)
            updateForecastVarianceCell(projectId, parseFloat(fiscalPeriodStr))

        }

        handleHideFinancialAndForecastingControls()
        updateSummaryRow();
        setIsDirty(false);
    }


    const handleSaveProjectFinancialAndForecasting = async () => {

        const storedChanges = getStoredChanges();
        const keys = Object.keys(storedChanges);
        const pFS: Pick<ClientProjectFinance, 'projectId' | 'fiscalPeriod' | 'plannedAmount' | 'forecastAmount'>[] = []
        // From the changes made, develop a list of projectFinance objects
        for (let i = 0; i < keys.length; i++) {
            const [projectId, fiscalPeriodStr] = keys[i].split("+");


            const pF = projectFinanceAndForecasting.find(pF => pF.fiscalPeriod === parseFloat(fiscalPeriodStr));

            const value = storedChanges[keys[i]];
            const newProjectFinance = {
                projectId: projectId,
                fiscalPeriod: parseFloat(fiscalPeriodStr),
                plannedAmount: pF?.plannedAmount ? pF.plannedAmount : 0,
                forecastAmount: value
            }

            pFS.push(newProjectFinance)
        }

        // Send them to the api to update
        const res = await updateProjectFinance(pFS);


        if (res) {
            // Commit
            // Merge changes with local
            res.forEach(cPF => {
                const index = projectFinanceAndForecasting.findIndex(pF =>
                    pF.projectId === cPF.projectId &&
                    pF.fiscalPeriod === cPF.fiscalPeriod
                )

                if (index === -1)
                    return;

                projectFinanceAndForecasting[index] = {
                    ...projectFinanceAndForecasting[index],
                    ...cPF
                }
            })


            setProjectFinanceAndForecasting([...projectFinanceAndForecasting])
            commitChanges();
            setIsDirty(false);
            handleHideFinancialAndForecastingControls()
        }
    }

    const handleRemoveChange = (
        projectId: string,
        fiscalPeriod: number
    ) => {
        const key = `${projectId}+${fiscalPeriod}`;

        let newValue = 0;
        const pF = projectFinanceAndForecasting.find(pF => pF.fiscalPeriod === fiscalPeriod);
        newValue = pF ? pF.forecastAmount : 0;

        const updatedChanges = removeChange(key, "$" + newValue);
        updateForecastVarianceCell(projectId, fiscalPeriod);

        // If updated changes are none hide controls
        if (Object.keys(updatedChanges).length === 0) {
            handleHideFinancialAndForecastingControls()
            setIsDirty(false);
        }

        updateSummaryRow();
    };

    const taskColumns: ColumnDef<ProjectTask>[] = useMemo(() => [
        { header: "Task", accessorKey: "code" },
        {
            header: "Start",
            accessorKey: "startDate",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => {
                const val = getValue()
                if (val === null) {
                    return "Not found"
                } else {
                    return String(getValue()).split("T")[0]
                }
            }
        },
        {
            header: "End",
            accessorKey: "endDate",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => {
                const val = getValue()
                if (val === null) {
                    return "Not found"
                } else {
                    return String(getValue()).split("T")[0]
                }
            }
        },
        {
            header: "Completed %",
            accessorKey: "completedPercentage",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => formatNumber(getValue(), 2) + "%"
        },
        {
            header: "Status",
            accessorKey: "status.description",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => <span className={ProjectTaskUI.getStatusColor(getValue() as string)}>{getValue() as string}</span>
        }
    ], [])



    const costColumns: ColumnDef<ProjectBudget>[] = [
        {
            header: "Task",
            accessorKey: "projectTask.code",
            size: 200,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            }
        },
        {
            header: "Item",
            accessorKey: "inventoryItem.code",
            minSize: 150,
            maxSize: 200,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            },
        },
        {
            minSize: 100,
            maxSize: 100,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            },
            header: "Account",
            accessorKey: "projectAccountGroup.description"
        },
        {
            size: 50,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            },
            header: "Units",
            accessorKey: "unitOfMeasureId",
            cell: ({ getValue }) => {
                const val = getValue();
                return val ? val : "Not Found"
            }
        },
        {
            header: "Unit Rate",
            accessorKey: "unitRate",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            size: 100,
            cell: ({ getValue }) => formatCurrency(getValue())
        },
        {
            header: "Rev Qty",
            accessorKey: "revQty",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ getValue }) => formatNumber(getValue(), 2)
        },
        {
            header: "Act Qty",
            accessorKey: "actualQty",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ row }) => {
                const og = row.original
                const more = Math.max(og.qty ?? 0, og.revQty ?? 0)

                // 75% more
                const ceiling = more * 1.75;
                const actualQty = og.actualQty ?? 0
                let className = ''

                if (more !== 0) {
                    if (actualQty > more)
                        className = 'error'
                    if (actualQty > ceiling)
                        className = 'warning'
                }

                return formatNumber(actualQty, 2)
            }
        },
        {
            header: "Rev Amt",
            accessorKey: "revisedAmount",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ getValue }) => formatCurrency(getValue())
        },
        {
            header: "Act Amt",
            accessorKey: "actualAmount",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ row }) => {
                const og = row.original;
                const more = Math.max(og.amount ?? 0, og.revisedAmount ?? 0)

                // 75% more
                const ceiling = more * 1.75;
                const actualAmount = og.actualAmount ?? 0
                let className = ''

                if (more !== 0) {
                    if (actualAmount > more)
                        className = 'error'
                    if (actualAmount > ceiling)
                        className = 'warning'
                }

                return formatCurrency(actualAmount)
            }
        }
    ]

    const revenueColumns: ColumnDef<ProjectBudget>[] = useMemo(() => [
        {

            header: "Task",
            accessorKey: "projectTask.code",
            size: 200,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            }
        },
        {
            header: "Account",
            accessorKey: "projectAccountGroup.description",
            minSize: 100,
            maxSize: 100,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            },
        },
        {
            size: 50,
            meta: {
                getCellStyle: (value: number) => ({
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                })
            },
            header: "Units",
            accessorKey: "unitOfMeasureId",
            cell: ({ getValue }) => {
                const val = getValue();
                return val ? val : "Not Found"
            }
        },
        {
            header: "Unit Rate",
            accessorKey: "unitRate",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            size: 100,
            cell: ({ getValue }) => formatCurrency(getValue())
        },
        {
            header: "Rev Qty",
            accessorKey: "revQty",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ getValue }) => formatNumber(getValue(), 2)
        },
        {
            header: "Act Qty",
            accessorKey: "actualQty",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ row }) => {
                const og = row.original
                const more = Math.max(og.qty ?? 0, og.revQty ?? 0)

                // 75% more
                const ceiling = more * 1.75;
                const actualQty = og.actualQty ?? 0
                let className = ''

                if (more !== 0) {
                    if (actualQty > more)
                        className = 'error'
                    if (actualQty > ceiling)
                        className = 'warning'
                }

                return formatNumber(actualQty, 2)
            }
        },
        {
            header: "Rev Amt",
            accessorKey: "revisedAmount",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ getValue }) => formatCurrency(getValue())
        },
        {
            header: "Act Amt",
            accessorKey: "actualAmount",
            meta: {
                getHeaderStyle: () => ({ textAlign: "right" }),
                getCellStyle: (value: number) => ({ textAlign: 'right' })
            },
            minSize: 90,
            maxSize: 100,
            cell: ({ row }) => {
                const og = row.original;
                const more = Math.max(og.amount ?? 0, og.revisedAmount ?? 0)

                // 75% more
                const ceiling = more * 1.75;
                const actualAmount = og.actualAmount ?? 0
                let className = ''

                if (more !== 0) {
                    if (actualAmount > more)
                        className = 'error'
                    if (actualAmount > ceiling)
                        className = 'warning'
                }

                return formatCurrency(actualAmount)
            }
        }
    ], [])

    const projectChangeLogColumns: ColumnDef<ChangeOrder>[] = useMemo(() => [
        { header: "CO#", accessorKey: "code" },
        { header: "Description / Status", accessorKey: "descriptoin" },
        {
            header: "Amount ($)",
            accessorKey: "amount",
            cell: ({ getValue }) => formatCurrency(getValue())
        },
        {
            header: "Executed",
            accessorKey: "executed",
            cell: ({ getValue }) => getValue() ? "Executed" : "Pending"
        },
    ], [])

    const financialAndForeCastingColumns: ColumnDef<FinancialAndForecastingRow>[] = useMemo(() => [
        {
            header: "Month",
            accessorKey: "period",
            cell: ({ row }) => {
                if (row.original.__rowType === 'summary') return <strong>Total</strong>;
                const calendar = new FiscalCalendar(DEFAULT_FISCAL_START);
                const fiscalYear = Math.floor(row.original.fiscalPeriod / 100)
                const period = row.original.fiscalPeriod % 100
                return calendar.keyToMonthString(fiscalYear + (period / 100))
            }
        },
        {
            header: "Actual ($)",
            accessorKey: "actualAmount",
            cell: ({ getValue, row }) => {
                if (row.original.__rowType === 'summary') return <strong>{formatCurrency(getValue())}</strong>;
                return formatCurrency(getValue())
            }
        },
        {
            header: "Baseline / Planned ($)",
            accessorKey: "plannedAmount",
            cell: ({ getValue, row }) => {
                if (row.original.__rowType === 'summary') return <strong>{formatCurrency(getValue())}</strong>;
                return <CurrencyInput
                    style={{ margin: '0px', minHeight: '0px', padding: '4px 8px' }}
                    defaultValue={"$" + getValue() as any}
                    inputClassName="finance-input"
                    data-row={row.index}
                    data-col="planned"
                    disabled
                />
            }
        },
        {
            header: 'Variance',
            accessorKey: "plannedVariance",
            cell: ({ row }) => {
                const variance = (row.original.actualAmount ?? 0) - (Number.isNaN(row.original.plannedAmount) ? 0 : row.original.plannedAmount);
                return <span className={ClientProjectFinanceUI.varianceColor(variance)}>{formatCurrency(variance)}</span>
            }
        },
        {
            header: "Forecast",
            accessorKey: "forecastAmount",
            cell: ({ getValue, row }) => {
                if (row.original.__rowType === 'summary') return <strong id="summary-forecast">{formatCurrency(getValue())}</strong>;

                const value = getValue();
                const inputId = `${row.original.projectId}+${row.original.fiscalPeriod}`
                return <CurrencyInput
                    id={inputId}
                    style={{ margin: '0px', minHeight: '0px', padding: '4px 8px' }}
                    defaultValue={"$" + (value as any)}
                    onCommit={(newValue) => {
                        if (row.original.__rowType === 'summary') return;
                        if (newValue === Number(value)) {
                            handleRemoveChange(row.original.projectId, row.original.fiscalPeriod);
                        } else {
                            handleAddChange(
                                row.original.projectId,
                                row.original.fiscalPeriod,
                                newValue ?? 0
                            );
                        }
                    }}
                    data-row={row.index}
                    inputClassName="finance-input"
                    data-col="forecast"
                    onKeyDown={(e) => {
                        if (e.key !== "Tab") return;
                        e.preventDefault();
                        const row = Number(e.currentTarget.dataset.row);
                        const col = e.currentTarget.dataset.col;
                        const nextRow = row + 1;
                        const selector = `.finance-input[data-row="${nextRow}"][data-col="${col}"]`;
                        const next = document.querySelector<HTMLInputElement>(selector);
                        next?.focus();
                    }}
                />
            }
        },
        {
            header: 'Variance',
            accessorKey: "forecastVariance",
            cell: ({ row }) => {
                const variance = (row.original.actualAmount ?? 0) - (Number.isNaN(row.original.forecastAmount) ? 0 : row.original.forecastAmount);

                if (row.original.__rowType === 'summary') {
                    return <strong id="summary-forecast-variance" className={ClientProjectFinanceUI.varianceColor(variance)}>
                        {formatCurrency(variance)}
                    </strong>
                }

                return <span
                    id={`forecast-variance-${row.original.fiscalPeriod}`}
                    className={ClientProjectFinanceUI.varianceColor(variance)}>
                    {formatCurrency(variance)}
                </span>
            }
        },
    ], [projectFinanceAndForecasting])

    const transactionColumns: ColumnDef<Transaction>[] = useMemo(() => [
        { header: "Task", accessorKey: "task.code" },
        {
            header: "Account",
            accessorKey: "transactionAccount.description",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => getValue() as string
        },
        {
            header: "Amount",
            accessorKey: "amount",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => <span className={TransactionUI.amountColor(Number(getValue()) * -1)}>{formatCurrency(Number(getValue()) * -1)}</span>
        },
        {
            header: "Billable",
            accessorKey: "billable",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => getValue() ? <CheckCircleIcon className="text-success" /> : <CloseCircleIcon className="text-danger" />
        },
        {
            header: "Fiscal Period",
            accessorKey: "fiscalPeriod",
            meta: {
                getHeaderStyle: () => ({
                    textAlign: "right",
                }),
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                }
            },
            cell: ({ getValue }) => {
                const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)
                const fiscalPeriod = getValue() as number
                const fiscalYear = Math.floor(fiscalPeriod / 100)
                const period = fiscalPeriod % 100
                // Calendar month + calendar year (Sep–Dec belong to the previous calendar year)
                return calendar.fiscalToLabel(fiscalYear, period)
            }
        }
    ], [])

    const taskTable = useDataTable({
        data: projectTasks,
        columns: taskColumns,
    })

    const revenueTable = useDataTable({
        data: revenueBudgets,
        columns: revenueColumns,
    })

    const costTable = useDataTable({
        data: costBudgets,
        columns: costColumns,
    })

    const transactionTable = useDataTable({
        data: projectTransactions,
        columns: transactionColumns,
    })

    const projectChangeLogArray = useMemo(() => [], [])

    const projectChangeLogTable = useDataTable({
        data: projectChangeLogArray,
        columns: projectChangeLogColumns,
    })

    // Has a Total (summary) row and DOM-computed totals: no sorting
    const financialAndForecastingTable = useDataTable({
        data: financialAndForecastingRows,
        columns: financialAndForeCastingColumns,
        enableSorting: false,
    })

    return <ProjectDetailsTemplate
        handleOpenHelpDocument={handleOpenManual}
        projectHealth={projectHealth}
        projectHealthHistory={projectHealthHistory}
        isLoadingProjectHealth={loadingProjectHealth}
        marginTarget={marginTarget}
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        isLoadingProject={loadingProject}
        project={project}
        isSyncingData={isSyncingData}
        handleSyncData={() => {
            if (projectId) {
                handleSyncData(projectId)
            }
        }}
        openSyncModal={openSyncModal}
        closeSyncMOdal={closeSyncModal}
        handleNavigateToAcumatica={handleNavigateToAcumatica}
        isSyncModalOpen={isSyncModalOpen}
        taskColumns={taskColumns}
        taskTable={taskTable}

        revenueColumns={revenueColumns}
        revenueTable={revenueTable}

        costColumns={costColumns}
        costTable={costTable}

        transactionTable={transactionTable}
        transactionColumns={transactionColumns}
        projectFinances={projectFinance}

        projectChangeLogColumns={projectChangeLogColumns}
        projectChangeLogTable={projectChangeLogTable}

        financialAndForecastingColumns={financialAndForeCastingColumns}
        financialAndForecastingTable={financialAndForecastingTable}
        financialAndForecastingControlsId={FINANCIALS_AND_FORECASTING_CONTROLS}
        transactionTotalItems={projectTransactions.length}
        handleSaveProjectFinancialAndForecasting={handleSaveProjectFinancialAndForecasting}
        handleResetProjectFinancialAndForecasting={handleResetProjectFinancialAndForecasting}
    />
}


export default withRouter(ProjectDetailsPage);